package er.prateek.crossly.ui.screens.results

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.spring
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import er.prateek.crossly.ads.AdPlacement
import er.prateek.crossly.ads.AdTrigger
import er.prateek.crossly.data.model.DifficultyTier
import er.prateek.crossly.data.model.GameMode
import er.prateek.crossly.di.AppContainer
import er.prateek.crossly.firebase.AnalyticsEvents
import er.prateek.crossly.game.riddle.RiddleRunEngine
import er.prateek.crossly.share.RiddleChallengeRef
import er.prateek.crossly.share.formatSpokenDuration
import er.prateek.crossly.share.riddleChallengeWon
import er.prateek.crossly.share.tierDisplayName
import er.prateek.crossly.share.timeMarginMs
import er.prateek.crossly.ui.components.ConfettiBurst
import er.prateek.crossly.ui.components.GameButton
import er.prateek.crossly.ui.components.GameButtonSize
import er.prateek.crossly.ui.components.GameButtonStyle
import er.prateek.crossly.ui.components.GameCard
import er.prateek.crossly.ui.components.GameScreen
import er.prateek.crossly.ui.components.IconPlay
import er.prateek.crossly.ui.components.IconReplay
import er.prateek.crossly.ui.components.IconTrophy
import er.prateek.crossly.ui.components.StarRating
import er.prateek.crossly.progression.ModeProgressionManager
import er.prateek.crossly.ui.share.NamePromptDialog
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game
import kotlinx.coroutines.delay

private const val REWARD_BEAT_MS = 1_400L

/** Mirrors the crossword results' `results_share`, so the name-prompt funnel splits by screen. */
private const val NAME_PROMPT_SOURCE = "mode_results_share"

/**
 * End screen for the leveled math modes. Reuses the crossword results' reward beat (trophy drop,
 * star stamp, confetti) but is content-free: it derives the next level straight from the level id,
 * so it needs none of the crossword content/share machinery.
 */
@Composable
fun ModeResultsScreen(
    mode: String,
    levelId: String,
    stars: Int,
    elapsedMs: Long,
    onReplay: (levelId: String) -> Unit,
    onNextLevel: (levelId: String) -> Unit,
    onBackToLevels: () -> Unit,
    /** Riddles only: the seed the set was played with, plus how many questions were right. */
    seed: Long = 0,
    correct: Int = 0,
    total: Int = 0,
    /** The run's own pass mark; `0` (older routes) falls back to the default ratio. */
    passMark: Int = 0,
    /** Set when this run was a friend's challenge. */
    challengerName: String? = null,
    challengerTimeMs: Long = 0,
    challengerScore: Int = 0,
) {
    val colors = MaterialTheme.game
    val gameMode = remember(mode) { runCatching { GameMode.valueOf(mode) }.getOrDefault(GameMode.MATH_CROSSWORD) }
    val tier = remember(levelId) {
        runCatching { DifficultyTier.valueOf(levelId.split("-")[1].uppercase()) }.getOrDefault(DifficultyTier.EASY)
    }
    val index = remember(levelId) { levelId.substringAfterLast("-").toIntOrNull() ?: 1 }

    val progression: ModeProgressionManager
    val levelsPerTier: Int
    val levelNoun: String
    if (gameMode == GameMode.RIDDLES) {
        progression = AppContainer.riddleProgressionManager
        levelsPerTier = AppContainer.appConfig().modes.riddles.levelsPerTier
        levelNoun = "Set"
    } else {
        progression = AppContainer.mathProgressionManager
        levelsPerTier = AppContainer.appConfig().modes.mathCrossword.levelsPerTier
        levelNoun = "Level"
    }
    // A riddle set below the pass mark comes back with 0 stars and recorded nothing.
    val failed = stars == 0
    // Offered only once this level is cleared (now or on an earlier run), so a failed set never
    // skips ahead.
    val nextLevelId = if (index < levelsPerTier && progression.starsFor(levelId) >= 1) {
        progression.levelId(tier, index + 1)
    } else {
        null
    }

    // Interstitial checkpoint, delayed past the reward beat so an ad never lands on the trophy.
    LaunchedEffect(levelId) {
        delay(REWARD_BEAT_MS)
        val trigger = if (gameMode == GameMode.RIDDLES) AdTrigger.RIDDLE_COMPLETED else AdTrigger.MATH_COMPLETED
        AppContainer.adsCoordinator.onPuzzleCompleted(trigger)
    }

    val trophyScale = remember(levelId) { Animatable(0f) }
    LaunchedEffect(levelId) {
        trophyScale.animateTo(1f, spring(dampingRatio = Spring.DampingRatioMediumBouncy, stiffness = Spring.StiffnessLow))
    }

    // Riddle challenges: win by clearing the set faster AND answering at least as many right.
    val isRiddles = gameMode == GameMode.RIDDLES
    val inChallenge = isRiddles && challengerName != null && challengerTimeMs > 0
    val wonChallenge = inChallenge && riddleChallengeWon(elapsedMs, correct, challengerTimeMs, challengerScore)
    val challengeVerdict = when {
        !inChallenge -> null
        wonChallenge -> "You beat $challengerName by ${formatSpokenDuration(timeMarginMs(elapsedMs, challengerTimeMs))}!"
        elapsedMs >= challengerTimeMs && correct < challengerScore ->
            "$challengerName wins: faster ${formatSpokenDuration(challengerTimeMs)} and more correct ($challengerScore vs $correct)"
        correct < challengerScore -> "$challengerName wins: you needed $challengerScore correct, got $correct"
        else -> "$challengerName was faster by ${formatSpokenDuration(timeMarginMs(challengerTimeMs, elapsedMs))}"
    }

    LaunchedEffect(levelId) {
        AppContainer.analytics.logEvent(
            AnalyticsEvents.RESULTS_VIEWED,
            buildMap {
                put("mode", gameMode.analyticsName)
                put("level_id", levelId)
                put("tier", tier.name)
                put("stars", stars)
                put("elapsed_ms", elapsedMs)
                put("has_next_level", nextLevelId != null)
                put("cleared", stars > 0)
                put("from_challenge", inChallenge)
                put("challenge_won", wonChallenge)
                if (isRiddles) {
                    put("correct", correct)
                    put("total", total)
                }
            },
        )
    }

    val share = AppContainer.shareManager
    var pendingShare by remember { mutableStateOf<String?>(null) }
    val challengeRef = RiddleChallengeRef(levelId, seed)
    val runShare: (String) -> Unit = { kind ->
        val name = share.playerName()
        val label = "Math Riddles ${tierDisplayName(tier)} Set $index"
        if (kind == "win" && challengerName != null) {
            share.shareWinWithReferrer(name, challengerName, challengeRef.puzzleId, label, elapsedMs, challengerTimeMs)
        } else {
            share.shareChallenge(name, challengeRef.puzzleId, label, elapsedMs, correct)
        }
    }

    // Same share funnel as the crossword results: tap -> (name prompt) -> SHARE_CREATED.
    val tapShare: (kind: String, analyticsKind: String) -> Unit = { kind, analyticsKind ->
        AppContainer.analytics.logEvent(
            AnalyticsEvents.SHARE_BUTTON_TAPPED,
            mapOf("mode" to gameMode.analyticsName, "level_id" to levelId, "kind" to analyticsKind),
        )
        pendingShare = kind
    }

    val headline = if (inChallenge) {
        if (wonChallenge) "You won!" else "You lost"
    } else when (stars) {
        3 -> "Flawless!"
        2 -> "Great!"
        1 -> "Solved!"
        else -> "So close!"
    }

    GameScreen(adPlacement = AdPlacement.RESULTS) {
        Box(modifier = Modifier.fillMaxSize()) {
            Column(
                modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(Spacing.lg, Alignment.CenterVertically),
            ) {
                Box(
                    modifier = Modifier.size(96.dp).graphicsLayer {
                        scaleX = trophyScale.value
                        scaleY = trophyScale.value
                        rotationZ = (1f - trophyScale.value) * -25f
                    },
                    contentAlignment = Alignment.Center,
                ) {
                    IconTrophy(size = 96.dp, tint = if (failed) colors.inkMuted else colors.gold)
                }

                Text(
                    text = headline,
                    style = MaterialTheme.typography.displaySmall,
                    color = colors.ink,
                    textAlign = TextAlign.Center,
                )

                StarRating(stars = stars, starSize = 46.dp, spacing = Spacing.sm, animated = true)

                GameCard(modifier = Modifier.widthIn(max = 420.dp).fillMaxWidth()) {
                    Text(
                        text = "$levelNoun $index",
                        style = MaterialTheme.typography.titleMedium,
                        color = colors.ink,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth(),
                    )
                    if (elapsedMs > 0) {
                        Text(
                            text = "Time ${formatClock(elapsedMs)}",
                            style = MaterialTheme.typography.titleMedium,
                            color = colors.gold,
                            textAlign = TextAlign.Center,
                            modifier = Modifier.fillMaxWidth().padding(top = Spacing.sm),
                        )
                    }
                }

                if (isRiddles && total > 0) {
                    Text(
                        text = "$correct / $total correct",
                        style = MaterialTheme.typography.titleMedium,
                        color = colors.ink,
                        textAlign = TextAlign.Center,
                    )
                    if (failed) {
                        Text(
                            text = "Get ${passMark.takeIf { it > 0 } ?: RiddleRunEngine.passMarkFor(total)} right to clear this set",
                            style = MaterialTheme.typography.bodyMedium,
                            color = colors.inkMuted,
                            textAlign = TextAlign.Center,
                        )
                    }
                }
                challengeVerdict?.let { verdict ->
                    Text(
                        text = verdict,
                        style = MaterialTheme.typography.titleMedium,
                        color = if (wonChallenge) colors.success else colors.danger,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.widthIn(max = 420.dp),
                    )
                }

                Column(
                    modifier = Modifier.widthIn(max = 420.dp).fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(Spacing.sm),
                ) {
                    // A failed set isn't something to challenge a friend with.
                    if (isRiddles && seed != 0L && !failed) {
                        if (wonChallenge) {
                            GameButton(
                                text = "Share with $challengerName",
                                onClick = { tapShare("win", "win_vs_referrer") },
                                style = GameButtonStyle.GOLD,
                                fillWidth = true,
                            )
                        }
                        GameButton(
                            text = if (inChallenge) "Challenge another friend" else "Challenge a friend",
                            onClick = { tapShare("challenge", if (inChallenge) "challenge_another" else "challenge_friend") },
                            style = if (wonChallenge) GameButtonStyle.SECONDARY else GameButtonStyle.GOLD,
                            fillWidth = true,
                        )
                    }
                    nextLevelId?.let { next ->
                        GameButton(
                            text = "Next $levelNoun",
                            onClick = { onNextLevel(next) },
                            style = GameButtonStyle.PRIMARY,
                            size = GameButtonSize.LARGE,
                            fillWidth = true,
                            icon = { tint -> IconPlay(size = 17.dp, tint = tint) },
                        )
                    }
                    GameButton(
                        text = if (failed) "Try Again" else "Play Again",
                        onClick = { onReplay(levelId) },
                        style = if (failed) GameButtonStyle.PRIMARY else GameButtonStyle.SECONDARY,
                        fillWidth = true,
                        icon = { tint -> IconReplay(size = 18.dp, tint = tint) },
                    )
                    GameButton(
                        text = "Back to Levels",
                        onClick = onBackToLevels,
                        style = GameButtonStyle.GHOST,
                        fillWidth = true,
                    )
                }
            }

            if (!failed) {
                ConfettiBurst(
                    modifier = Modifier.fillMaxSize(),
                    seed = levelId.hashCode() + stars,
                    pieceCount = if (stars >= 3) 72 else 44,
                )
            }
        }
    }

    val kind = pendingShare
    if (kind != null && !share.hasPlayerName()) {
        LaunchedEffect(kind) {
            AppContainer.analytics.logEvent(AnalyticsEvents.NAME_PROMPT_SHOWN, mapOf("source" to NAME_PROMPT_SOURCE))
        }
        NamePromptDialog(
            initialName = share.playerName(),
            skipAllowed = false,
            onSave = { name ->
                share.savePlayerName(name)
                AppContainer.analytics.logEvent(AnalyticsEvents.NAME_SAVED, mapOf("source" to NAME_PROMPT_SOURCE))
                runShare(kind)
                pendingShare = null
            },
            onSkip = {
                AppContainer.analytics.logEvent(AnalyticsEvents.NAME_SKIPPED, mapOf("source" to NAME_PROMPT_SOURCE))
                pendingShare = null
            },
            onDismiss = { pendingShare = null },
        )
    } else if (kind != null) {
        LaunchedEffect(kind) {
            runShare(kind)
            pendingShare = null
        }
    }
}

private fun formatClock(ms: Long): String {
    val totalSeconds = ms / 1000
    return "${totalSeconds / 60}:${(totalSeconds % 60).toString().padStart(2, '0')}"
}
